/**
 * EntityAdminPage — panel scoped por entidad (multi-tenant fase B3).
 *
 * Rutas:
 *   /o/:slug/admin           → sin token: form magic-link (envía email al
 *                              contact_email de la entidad)
 *   /o/:slug/admin?t=<token> → con token: dashboard entidad (branding +
 *                              shows propios)
 *
 * Cada organizador gestiona SUS shows sin depender del operador principal.
 * El token viene del email magic-link que se envía al contact_email.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/client';
import EntityThemeProvider from '../components/EntityThemeProvider';
import BrandingStudio from '../components/BrandingStudio';

interface EntityAdminView {
  id: string;
  slug: string;
  name: string;
  contact_email: string;
  contact_name?: string | null;
  contact_phone?: string | null;
  description?: string | null;
  entity_type?: string | null;
  city?: string | null;
  province?: string | null;
  logo_url?: string | null;
  cover_url?: string | null;
  accent_color?: string | null;
  website_url?: string | null;
  instagram_url?: string | null;
  public_url: string;
  admin_url: string;
}

interface Show {
  id: string;
  slug: string;
  title: string;
  event_datetime: string;
  status: string;
  is_public: boolean;
  tickets_sold: number;
  tickets_available?: number | null;
  capacity?: number | null;
  ticket_price?: number | null;
  ticket_price_min?: number | null;
}

export default function EntityAdminPage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  if (!token) return <MagicLinkRequest slug={slug} />;
  return <EntityAdminApp slug={slug} token={token} />;
}

/* ── Solicitud de magic link ────────────────────────────────── */

function MagicLinkRequest({ slug }: { slug: string }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = `Admin ${slug} · Pagafácil`; }, [slug]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post(`/public/orgs/${slug}/lookup`, { email });
      setSent(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Error al enviar el enlace');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={shellStyle}>
      <Header slug={slug} showAdminLink={false} />
      <main style={{ maxWidth: 480, margin: '80px auto', padding: '0 24px' }}>
        <div style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 16, padding: 32 }}>
          <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.55 }}>
            Admin organizador
          </div>
          <h1 style={{ fontFamily: '"Spectral", serif', fontSize: 32, margin: '8px 0 20px', fontWeight: 700 }}>
            Accede al panel
          </h1>

          {sent ? (
            <div style={{ padding: 20, background: '#E8EEDC', border: '1px solid #C7D5A8', borderRadius: 10, color: '#46602C' }}>
              <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>
                Enlace enviado
              </div>
              Si el email corresponde al contacto de <b>/o/{slug}</b>, en 1-2 min recibirás un enlace mágico para entrar.
              Caduca en 60 min.
            </div>
          ) : (
            <form onSubmit={submit}>
              <p style={{ fontSize: 14, opacity: 0.75, marginTop: 0 }}>
                Introduce el email de contacto de la entidad. Te enviamos un enlace de acceso al email registrado.
              </p>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 20 }}>
                <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.7 }}>Email de contacto</span>
                <input
                  type="email" required autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ padding: '12px 14px', border: '1px solid #EAE1D2', borderRadius: 8, fontSize: 16, background: '#FBF7F0' }}
                />
              </label>
              <button
                type="submit" disabled={sending || !email}
                style={{
                  marginTop: 20, width: '100%', padding: '14px', borderRadius: 12,
                  background: sending || !email ? '#B9B0A0' : '#1C5D82',
                  color: '#FFFFFF', border: 'none', fontSize: 16, fontWeight: 700,
                  cursor: sending || !email ? 'not-allowed' : 'pointer',
                }}
              >
                {sending ? 'Enviando…' : 'Enviarme el enlace'}
              </button>
            </form>
          )}
        </div>
        <div style={{ marginTop: 16, fontSize: 12, opacity: 0.6, textAlign: 'center' }}>
          <Link to={`/o/${slug}`} style={{ color: 'inherit' }}>← Ver landing pública</Link>
        </div>
      </main>
    </div>
  );
}

/* ── Panel scoped ───────────────────────────────────────────── */

type Tab = 'shows' | 'rrpps' | 'branding';

function EntityAdminApp({ slug, token }: { slug: string; token: string }) {
  const [entity, setEntity] = useState<EntityAdminView | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [tab, setTab] = useState<Tab>('shows');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([
        api.get<EntityAdminView>(`/public/orgs/${slug}/admin?t=${encodeURIComponent(token)}`),
        api.get<Show[]>(`/public/orgs/${slug}/admin/shows?t=${encodeURIComponent(token)}`),
      ]);
      setEntity(e.data);
      setShows(s.data || []);
      document.title = `Admin · ${e.data.name}`;
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Enlace inválido o caducado');
    }
  }, [slug, token]);

  useEffect(() => { void load(); }, [load]);

  if (error) {
    return (
      <div style={shellStyle}>
        <Header slug={slug} showAdminLink={false} />
        <main style={{ maxWidth: 480, margin: '80px auto', padding: '0 24px' }}>
          <div style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 16, padding: 32 }}>
            <h1 style={{ fontFamily: '"Spectral", serif', fontSize: 24, margin: 0 }}>Sin acceso</h1>
            <p style={{ opacity: 0.7 }}>{error}</p>
            <Link to={`/o/${slug}/admin`} style={{ color: '#1C5D82', fontWeight: 700 }}>← Pedir otro enlace</Link>
          </div>
        </main>
      </div>
    );
  }

  if (!entity) {
    return <div style={shellStyle}><div style={{ padding: 64, opacity: 0.6, textAlign: 'center' }}>Cargando…</div></div>;
  }

  return (
    <EntityThemeProvider accentColor={entity.accent_color}>
      <div style={shellStyle}>
        <Header slug={slug} entity={entity} showAdminLink={false} />

        <div style={{ background: '#FFFFFF', borderBottom: '1px solid #EAE1D2' }}>
          <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 4 }}>
            <TabButton active={tab === 'shows'} onClick={() => setTab('shows')}>
              Eventos ({shows.length})
            </TabButton>
            <TabButton active={tab === 'rrpps'} onClick={() => setTab('rrpps')}>
              RRPPs y listas
            </TabButton>
            <TabButton active={tab === 'branding'} onClick={() => setTab('branding')}>
              Branding
            </TabButton>
          </div>
        </div>

        <main style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px 80px' }}>
          {tab === 'shows' && <ShowsSection entity={entity} token={token} shows={shows} onReload={load} />}
          {tab === 'rrpps' && <RrppsSection entity={entity} token={token} shows={shows} />}
          {tab === 'branding' && <BrandingSection entity={entity} token={token} onSaved={load} />}
        </main>
      </div>
    </EntityThemeProvider>
  );
}

/* ── Sección Eventos ────────────────────────────────────────── */

function ShowsSection({ entity, token, shows, onReload }: {
  entity: EntityAdminView; token: string; shows: Show[]; onReload: () => void;
}) {
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.55 }}>{entity.name}</div>
          <h2 style={{ fontFamily: '"Spectral", serif', fontSize: 28, margin: '6px 0 0' }}>Tus eventos</h2>
        </div>
        <button
          onClick={() => setCreating(true)}
          style={{ padding: '10px 18px', background: 'var(--brand)', color: 'var(--brand-ink)', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
        >+ Nuevo evento</button>
      </div>

      {creating && (
        <ShowQuickCreate
          entitySlug={entity.slug}
          token={token}
          onCancel={() => setCreating(false)}
          onCreated={() => { setCreating(false); onReload(); }}
        />
      )}

      {shows.length === 0 && !creating ? (
        <div style={{ padding: 40, background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 12, textAlign: 'center' }}>
          <div style={{ fontFamily: '"Spectral", serif', fontSize: 20 }}>Sin eventos todavía</div>
          <div style={{ opacity: 0.65, marginTop: 6 }}>Pulsa "+ Nuevo evento" para crear tu primero.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {shows.map((s) => <ShowRow key={s.id} show={s} entitySlug={entity.slug} token={token} />)}
        </div>
      )}
    </div>
  );
}

function ShowRow({ show, entitySlug: _entitySlug, token }: { show: Show; entitySlug: string; token: string }) {
  const dateStr = show.event_datetime
    ? new Date(show.event_datetime).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })
    : '';
  const statusBg: Record<string, string> = {
    draft: '#EAE1D2', on_sale: '#C7D5A8', sold_out: '#26201A', cancelled: '#F6E0DC', completed: '#EAE1D2',
  };
  const statusLabel: Record<string, string> = {
    draft: 'Borrador', on_sale: 'En venta', sold_out: 'Agotado', cancelled: 'Cancelado', completed: 'Cerrado',
  };
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 12, padding: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: '"Spectral", serif', fontSize: 18, fontWeight: 700 }}>{show.title}</div>
          <span style={{ fontSize: 10, fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, background: statusBg[show.status] || '#EAE1D2' }}>
            {statusLabel[show.status] || show.status}
          </span>
          {show.is_public && (
            <span style={{ fontSize: 10, fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, background: '#F4E3A6' }}>Público</span>
          )}
        </div>
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>{dateStr} · /e/{show.slug}</div>
        <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>
          Vendidas: <b>{show.tickets_sold}</b>{show.capacity != null && ` / ${show.capacity}`}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Editor completo — mismo admin_token, EventosAdminPage filtra por scope
            de la entidad automáticamente (backend `resolve_admin_scope`). Sin esto,
            desde EntityAdminPage sólo hay quick-create y los eventos existentes no
            se podían editar (ni subir cartel, ni descripción larga, etc.) —
            feedback Ana 31-jul. */}
        <Link
          to={`/operador/eventos?t=${encodeURIComponent(token)}`}
          target="_blank"
          style={{
            fontSize: 13, textDecoration: 'none',
            padding: '6px 12px', border: '1px solid #1C5D82', color: '#1C5D82',
            borderRadius: 6, fontWeight: 600, background: '#FFF',
          }}
        >
          ✏️ Editar
        </Link>
        <Link
          to={`/e/${show.slug}`}
          target="_blank"
          style={{ color: '#1C5D82', fontSize: 13, textDecoration: 'none' }}
        >
          Ver landing →
        </Link>
      </div>
    </div>
  );
}

function ShowQuickCreate({ entitySlug, token, onCancel, onCreated }: {
  entitySlug: string; token: string; onCancel: () => void; onCreated: () => void;
}) {
  const [f, setF] = useState({
    slug: '', title: '', event_datetime: '', ticket_price: '', capacity: '',
    venue_name: '', venue_city: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/public/orgs/${entitySlug}/admin/shows?t=${encodeURIComponent(token)}`, {
        slug: f.slug.trim().toLowerCase(),
        title: f.title.trim(),
        event_datetime: f.event_datetime,
        ticket_price: f.ticket_price ? Number(f.ticket_price) : null,
        capacity: f.capacity ? Number(f.capacity) : null,
        venue_name: f.venue_name || null,
        venue_city: f.venue_city || null,
        is_public: false,   // requiere activación explícita en el editor completo
      });
      toast.success('Evento creado en borrador');
      onCreated();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Error al crear');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ fontFamily: '"Spectral", serif', fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Nuevo evento (rápido)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Q label="Slug URL" value={f.slug} onChange={(v) => setF({ ...f, slug: v })} placeholder="mi-evento-2026" required />
        <Q label="Título" value={f.title} onChange={(v) => setF({ ...f, title: v })} required />
        <Q label="Fecha y hora" type="datetime-local" value={f.event_datetime} onChange={(v) => setF({ ...f, event_datetime: v })} required />
        <Q label="Precio (€)" type="number" step="0.01" value={f.ticket_price} onChange={(v) => setF({ ...f, ticket_price: v })} />
        <Q label="Aforo" type="number" value={f.capacity} onChange={(v) => setF({ ...f, capacity: v })} />
        <Q label="Local" value={f.venue_name} onChange={(v) => setF({ ...f, venue_name: v })} />
        <Q label="Ciudad" value={f.venue_city} onChange={(v) => setF({ ...f, venue_city: v })} />
      </div>
      <div style={{ marginTop: 12, fontSize: 12, opacity: 0.7 }}>
        Se crea en borrador. Después edítalo para añadir descripción, cartel, y activar "Visible en web + En venta".
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="button" onClick={onCancel} style={{ padding: '10px 16px', border: '1px solid #EAE1D2', background: '#FFF', borderRadius: 8, cursor: 'pointer' }}>Cancelar</button>
        <button type="submit" disabled={busy} style={{ padding: '10px 20px', border: 'none', background: 'var(--brand)', color: 'var(--brand-ink)', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Creando…' : 'Crear borrador'}
        </button>
      </div>
    </form>
  );
}

/* ── Sección Branding ───────────────────────────────────────── */

function BrandingSection({ entity, token, onSaved }: {
  entity: EntityAdminView; token: string; onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [extras, setExtras] = useState({
    contact_name: entity.contact_name || '',
    contact_phone: entity.contact_phone || '',
    entity_type: entity.entity_type || '',
    city: entity.city || '',
    province: entity.province || '',
  });

  const persist = async (branding: {
    name: string; description: string; logo_url: string; cover_url: string;
    accent_color: string; website_url: string; instagram_url: string;
  }) => {
    setBusy(true);
    try {
      const payload: Record<string, string | null> = {
        name: branding.name || null,
        description: branding.description || null,
        logo_url: branding.logo_url || null,
        cover_url: branding.cover_url || null,
        accent_color: branding.accent_color || null,
        website_url: branding.website_url || null,
        instagram_url: branding.instagram_url || null,
        contact_name: extras.contact_name || null,
        contact_phone: extras.contact_phone || null,
        entity_type: extras.entity_type || null,
        city: extras.city || null,
        province: extras.province || null,
      };
      await api.patch(`/public/orgs/${entity.slug}/admin?t=${encodeURIComponent(token)}`, payload);
      toast.success('Guardado');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <BrandingStudio
        entity={entity as any}
        onSave={persist}
        busy={busy}
      />

      <details style={{ marginTop: 20, background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 12, padding: 16 }}>
        <summary style={{ cursor: 'pointer', fontFamily: '"Space Mono", monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.7 }}>
          Datos administrativos (contacto, ciudad, tipo…)
        </summary>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
          <Q label="Tipo" value={extras.entity_type} onChange={(v) => setExtras({ ...extras, entity_type: v })} placeholder="hermandad, ampa..." />
          <Q label="Ciudad" value={extras.city} onChange={(v) => setExtras({ ...extras, city: v })} />
          <Q label="Provincia" value={extras.province} onChange={(v) => setExtras({ ...extras, province: v })} />
          <Q label="Nombre contacto" value={extras.contact_name} onChange={(v) => setExtras({ ...extras, contact_name: v })} />
          <Q label="Teléfono" value={extras.contact_phone} onChange={(v) => setExtras({ ...extras, contact_phone: v })} />
        </div>
        <div style={{ fontSize: 12, opacity: 0.55, marginTop: 10 }}>
          Estos campos se guardan al pulsar "Guardar cambios" arriba, junto con el branding.
        </div>
      </details>
    </div>
  );
}

/* ── Compartidos ────────────────────────────────────────────── */

function Header({ slug, entity }: { slug: string; entity?: EntityAdminView; showAdminLink?: boolean }) {
  const hasEntity = !!entity;
  return (
    <header style={{
      background: hasEntity ? 'var(--brand)' : '#0A0A0A',
      color: hasEntity ? 'var(--brand-ink)' : '#FFFFFF',
      padding: '18px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
    }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', gap: 8, color: 'inherit', textDecoration: 'none',
          fontFamily: "'Archivo', sans-serif", fontSize: 14, fontWeight: 900, letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}>
          <span style={{ width: 8, height: 8, background: 'currentColor', opacity: 0.85, borderRadius: '50%' }} />
          Pagafácil
        </Link>
        <span style={{ opacity: 0.45 }}>·</span>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85,
        }}>
          {entity ? `${entity.name} · admin` : `/o/${slug}/admin`}
        </span>
      </div>
      <Link to={`/o/${slug}`} style={{
        color: 'inherit', textDecoration: 'none',
        fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
        letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85,
      }}>
        Ver landing pública ↗
      </Link>
    </header>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '16px 20px', background: 'transparent', border: 'none',
        borderBottom: active ? `3px solid var(--brand)` : '3px solid transparent',
        color: active ? 'var(--brand)' : '#6B6660',
        fontFamily: "'Archivo', sans-serif", fontSize: 12, textTransform: 'uppercase',
        letterSpacing: '0.16em', fontWeight: 800, cursor: 'pointer',
        transition: 'color 0.15s',
      }}
    >{children}</button>
  );
}

interface QProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  placeholder?: string;
  required?: boolean;
}
function Q({ label, value, onChange, type = 'text', step, placeholder, required }: QProps) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.7 }}>
        {label}{required && ' *'}
      </span>
      <input
        type={type} step={step} required={required} placeholder={placeholder}
        value={value} onChange={(e) => onChange(e.target.value)}
        style={{ padding: '10px 12px', border: '1px solid #EAE1D2', borderRadius: 8, fontSize: 14, background: '#FBF7F0' }}
      />
    </label>
  );
}

const shellStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', system-ui, sans-serif",
  background: '#FAF7F4',
  color: '#0A0A0A',
  minHeight: '100vh',
};


/* ── Sección RRPPs y guest lists ────────────────────────── */

interface Rrpp {
  id: string;
  show_id: string;
  entity_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  sale_token: string;
  free_invites_total: number;
  free_invites_used: number;
  default_invite_price: number;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  share_url: string;
  list_url: string;
  invites_total: number;
  invites_confirmed: number;
  invites_pending: number;
  invites_used: number;
}

interface RrppInvite {
  id: string;
  entity_rrpp_id: string;
  token: string;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  quantity: number;
  price_override: number;
  is_used: boolean;
  is_confirmed: boolean;
  signup_source: string | null;
  created_at: string;
  share_url: string;
}

function RrppsSection({ entity, token, shows }: { entity: EntityAdminView; token: string; shows: Show[] }) {
  const [rrpps, setRrpps] = useState<Rrpp[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [invitesModal, setInvitesModal] = useState<Rrpp | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<Rrpp[]>(`/public/orgs/${entity.slug}/admin/rrpps?t=${encodeURIComponent(token)}`);
      setRrpps(r.data || []);
    } catch {
      toast.error('Error cargando RRPPs');
    } finally { setLoading(false); }
  }, [entity.slug, token]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.55 }}>{entity.name}</div>
          <h2 style={{ fontFamily: '"Spectral", serif', fontSize: 28, margin: '6px 0 0' }}>RRPPs y guest lists</h2>
          <p style={{ opacity: 0.7, fontSize: 13, marginTop: 6, maxWidth: 620 }}>
            Cada RRPP tiene un enlace público para que los guests se apunten a su lista. Tú confirmas desde aquí las que quieras y les llega email para canjear su entrada.
          </p>
        </div>
        {shows.length > 0 && (
          <button
            onClick={() => setCreating(true)}
            style={{ padding: '10px 18px', background: 'var(--brand)', color: 'var(--brand-ink)', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          >+ Nuevo RRPP</button>
        )}
      </div>

      {creating && (
        <RrppQuickCreate
          entity={entity} token={token} shows={shows}
          onCancel={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); }}
        />
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Cargando…</div>
      ) : rrpps.length === 0 ? (
        <div style={{ padding: 40, background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 12, textAlign: 'center' }}>
          <div style={{ fontFamily: '"Spectral", serif', fontSize: 20 }}>Sin RRPPs todavía</div>
          <div style={{ opacity: 0.65, marginTop: 6 }}>
            {shows.length === 0
              ? 'Necesitas al menos un evento para poder crear un RRPP. Ve a la pestaña Eventos.'
              : 'Pulsa "+ Nuevo RRPP" para crear el primero y compartir su enlace.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {rrpps.map(r => {
            const show = shows.find(s => s.id === r.show_id);
            return (
              <div key={r.id} style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 12, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontFamily: '"Spectral", serif', fontSize: 18, fontWeight: 700 }}>{r.name}</div>
                    {r.email && <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{r.email}</div>}
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                      Evento: <b>{show?.title || '—'}</b>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12 }}>
                      <span style={{ background: '#F4E3A6', padding: '2px 8px', borderRadius: 4 }}>
                        Pendientes: <b>{r.invites_pending}</b>
                      </span>
                      <span style={{ background: '#C7D5A8', padding: '2px 8px', borderRadius: 4 }}>
                        Confirmadas: <b>{r.invites_confirmed}</b>
                      </span>
                      <span style={{ background: '#E8EEDC', padding: '2px 8px', borderRadius: 4 }}>
                        Canjeadas: <b>{r.invites_used}</b>
                      </span>
                      {r.free_invites_total > 0 && (
                        <span style={{ opacity: 0.6 }}>Cupo: {r.invites_total}/{r.free_invites_total}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setInvitesModal(r)}
                    style={{ padding: '8px 14px', background: 'var(--brand)', color: 'var(--brand-ink)', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                  >
                    Ver guest list →
                  </button>
                </div>
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#FBF7F0', border: '1px solid #EAE1D2', borderRadius: 6, display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                  <span style={{ opacity: 0.6, flexShrink: 0 }}>Link:</span>
                  <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.list_url}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(r.list_url); toast.success('Copiado'); }}
                    style={{ padding: '3px 10px', border: '1px solid #EAE1D2', background: '#FFF', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                  >Copiar</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {invitesModal && (
        <RrppInvitesModal
          entity={entity} token={token} rrpp={invitesModal}
          onClose={() => setInvitesModal(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function RrppQuickCreate({ entity, token, shows, onCancel, onCreated }: {
  entity: EntityAdminView; token: string; shows: Show[]; onCancel: () => void; onCreated: () => void;
}) {
  const [f, setF] = useState({
    show_id: shows[0]?.id || '', name: '', email: '', phone: '',
    free_invites_total: 20, default_invite_price: 0, notes: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !f.show_id || !f.name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/public/orgs/${entity.slug}/admin/rrpps?t=${encodeURIComponent(token)}`, {
        show_id: f.show_id, name: f.name.trim(), email: f.email || null, phone: f.phone || null,
        free_invites_total: Number(f.free_invites_total) || 0,
        default_invite_price: Number(f.default_invite_price) || 0,
        notes: f.notes || null,
      });
      toast.success('RRPP creado');
      onCreated();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Error al crear');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{ background: '#FFFFFF', border: '1px solid #EAE1D2', borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ fontFamily: '"Spectral", serif', fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Nuevo RRPP</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.7 }}>Evento *</span>
          <select value={f.show_id} onChange={(e) => setF({ ...f, show_id: e.target.value })} required
            style={{ padding: '10px 12px', border: '1px solid #EAE1D2', borderRadius: 8, fontSize: 14, background: '#FBF7F0' }}>
            {shows.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </label>
        <Q label="Nombre" value={f.name} onChange={(v) => setF({ ...f, name: v })} required />
        <Q label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} />
        <Q label="Teléfono" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />
        <Q label="Cupo (0 = sin límite)" type="number" value={String(f.free_invites_total)} onChange={(v) => setF({ ...f, free_invites_total: Number(v) || 0 })} />
        <Q label="Precio por entrada (€)" type="number" step="0.01" value={String(f.default_invite_price)} onChange={(v) => setF({ ...f, default_invite_price: Number(v) || 0 })} />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="button" onClick={onCancel} style={{ padding: '10px 16px', border: '1px solid #EAE1D2', background: '#FFF', borderRadius: 8, cursor: 'pointer' }}>Cancelar</button>
        <button type="submit" disabled={busy} style={{ padding: '10px 20px', border: 'none', background: 'var(--brand)', color: 'var(--brand-ink)', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Creando…' : 'Crear RRPP'}
        </button>
      </div>
    </form>
  );
}

function RrppInvitesModal({ entity, token, rrpp, onClose, onChanged }: {
  entity: EntityAdminView; token: string; rrpp: Rrpp; onClose: () => void; onChanged: () => void;
}) {
  const [invites, setInvites] = useState<RrppInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<'pending' | 'confirmed'>('pending');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<RrppInvite[]>(`/public/orgs/${entity.slug}/admin/rrpps/${rrpp.id}/invites?t=${encodeURIComponent(token)}`);
      setInvites(r.data || []);
    } catch { toast.error('Error cargando invitaciones'); }
    finally { setLoading(false); }
  }, [entity.slug, token, rrpp.id]);

  useEffect(() => { void load(); }, [load]);

  const confirmSelected = async () => {
    if (selected.size === 0 || busy) return;
    setBusy(true);
    try {
      const r = await api.post<{ confirmed: number; skipped: number; email_errors: number }>(
        `/public/orgs/${entity.slug}/admin/rrpps/${rrpp.id}/invites/bulk-confirm?t=${encodeURIComponent(token)}`,
        Array.from(selected),
      );
      toast.success(`${r.data.confirmed} confirmadas · ${r.data.email_errors} emails fallaron`);
      setSelected(new Set());
      await load();
      onChanged();
    } catch { toast.error('Error al confirmar'); }
    finally { setBusy(false); }
  };

  const filtered = invites.filter(i => tab === 'pending' ? !i.is_confirmed : i.is_confirmed);
  const pendingCount = invites.filter(i => !i.is_confirmed).length;
  const confirmedCount = invites.filter(i => i.is_confirmed).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: '#FFF', borderRadius: 12, width: '100%', maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #EAE1D2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.6, fontFamily: '"Space Mono", monospace' }}>Guest list</div>
            <div style={{ fontFamily: '"Spectral", serif', fontSize: 20, fontWeight: 700 }}>{rrpp.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', opacity: 0.6 }}>×</button>
        </div>

        <div style={{ padding: '10px 20px', borderBottom: '1px solid #EAE1D2', display: 'flex', gap: 4 }}>
          <button onClick={() => setTab('pending')}
            style={{ padding: '8px 14px', border: 'none', background: tab === 'pending' ? 'var(--brand)' : 'transparent', color: tab === 'pending' ? 'var(--brand-ink)' : '#666', cursor: 'pointer', fontWeight: 600, fontSize: 13, borderRadius: 6 }}>
            Pendientes ({pendingCount})
          </button>
          <button onClick={() => setTab('confirmed')}
            style={{ padding: '8px 14px', border: 'none', background: tab === 'confirmed' ? 'var(--brand)' : 'transparent', color: tab === 'confirmed' ? 'var(--brand-ink)' : '#666', cursor: 'pointer', fontWeight: 600, fontSize: 13, borderRadius: 6 }}>
            Confirmadas ({confirmedCount})
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Cargando…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>
              {tab === 'pending' ? 'Sin invitaciones pendientes.' : 'Sin invitaciones confirmadas.'}
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #EAE1D2' }}>
                  {tab === 'pending' && (
                    <th style={{ padding: 8, textAlign: 'left', width: 30 }}>
                      <input type="checkbox"
                        checked={selected.size === filtered.length && filtered.length > 0}
                        onChange={(e) => setSelected(new Set(e.target.checked ? filtered.map(i => i.id) : []))}
                      />
                    </th>
                  )}
                  <th style={{ padding: 8, textAlign: 'left' }}>Nombre</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Email</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Tel</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Cant.</th>
                  <th style={{ padding: 8 }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(i => (
                  <tr key={i.id} style={{ borderBottom: '1px solid #F5F0E4' }}>
                    {tab === 'pending' && (
                      <td style={{ padding: 8 }}>
                        <input type="checkbox" checked={selected.has(i.id)}
                          onChange={(e) => {
                            const n = new Set(selected);
                            if (e.target.checked) n.add(i.id); else n.delete(i.id);
                            setSelected(n);
                          }}
                        />
                      </td>
                    )}
                    <td style={{ padding: 8, fontWeight: 600 }}>{i.guest_name || '—'}</td>
                    <td style={{ padding: 8, opacity: 0.7 }}>{i.guest_email || '—'}</td>
                    <td style={{ padding: 8, opacity: 0.7 }}>{i.guest_phone || '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{i.quantity}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {i.is_used ? <span style={{ fontSize: 11, color: '#137a3e', fontWeight: 700 }}>✓ Canjeada</span>
                        : i.is_confirmed ? <span style={{ fontSize: 11, opacity: 0.7 }}>Enviada</span>
                        : <span style={{ fontSize: 11, opacity: 0.6 }}>Pendiente</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {tab === 'pending' && selected.size > 0 && (
          <div style={{ padding: 12, borderTop: '1px solid #EAE1D2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FBF7F0' }}>
            <span style={{ fontSize: 13, opacity: 0.7 }}>{selected.size} seleccionadas</span>
            <button onClick={confirmSelected} disabled={busy}
              style={{ padding: '10px 18px', background: 'var(--brand)', color: 'var(--brand-ink)', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              {busy ? 'Enviando…' : `✓ Confirmar ${selected.size} y enviar emails`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
